// OTA-safe Live Activity controller.
//
// Centralizes tracking + upload signals into a single state machine, then
// drives the shipped iOS LiveActivityBridge (start / update({isTracking}) /
// end). All bridge calls are no-ops on non-iOS and never throw.
//
// Visible Dynamic Island / Lock Screen content (trip name, counts, warnings,
// elapsed time) cannot be added OTA in this build — the shipped widget
// extension only reads `isTracking`. That work is tracked separately as a
// Requires-App-Store-submission item.

import { Capacitor } from "@capacitor/core";
import {
  startTrackingLiveActivity,
  updateTrackingLiveActivity,
  endTrackingLiveActivity,
  endOrphanLiveActivitiesIfSafe,
  probeLiveActivity,
  type LiveActivityStats,
} from "@/lib/native/live-activity";
import {
  deriveLAState,
  mapToBridgeIsTracking,
  type LAState,
  type TrackingPhase,
  type UploadPhase,
} from "./state";
import { getGpsAdaptive, getGpsPreset, getLiveActivityEnabled, type GpsPreset } from "@/lib/prefs";
import { getGpsStatus } from "@/lib/native/gps-status";
import { getOutboxStats } from "@/lib/native/ping-outbox";
import { addConnectionListener, getConnectionType, type ConnectionType } from "@/lib/native/network";

// Cached connection type — updated by the listener installed in init(). Used
// synchronously by buildStatsSnapshot so the Live Activity payload can carry
// an isOffline flag without an async hop on every push.
let cachedConnection: ConnectionType = "unknown";

const PRESET_LABELS: Record<GpsPreset, string> = {
  battery_saver: "Battery saver",
  balanced: "Balanced",
  high_detail: "High detail",
  expedition: "Expedition",
  custom: "Custom",
  plane: "Plane",
};

function buildStatsSnapshot(): LiveActivityStats {
  const stats: LiveActivityStats = {};
  try {
    const adaptive = getGpsAdaptive();
    if (adaptive?.stoppedIntervalMs) {
      stats.intervalSeconds = Math.max(1, Math.round(adaptive.stoppedIntervalMs / 1000));
    }
  } catch { /* ignore */ }
  try {
    const preset = getGpsPreset();
    if (preset) {
      stats.presetKey = preset;
      if (PRESET_LABELS[preset]) stats.presetLabel = PRESET_LABELS[preset];
    }
  } catch { /* ignore */ }
  try {
    const gps = getGpsStatus();
    if (gps.lastFixAt) {
      const ageMs = Date.now() - gps.lastFixAt;
      stats.lastFixAgeSeconds = Math.max(0, Math.round(ageMs / 1000));
    }
  } catch { /* ignore */ }
  try {
    const ob = getOutboxStats();
    if (typeof ob.queued === "number") stats.queueDepth = Math.max(0, ob.queued);
  } catch { /* ignore */ }
  // Offline flag — `none` is the only definitive "no usable connection" value.
  // `unknown` is intentionally NOT treated as offline (avoids false-positives
  // on web / cold-boot before the first connection event arrives).
  stats.isOffline = cachedConnection === "none";
  return stats;
}


// Bucket values so a slow drift in last-fix age doesn't burn ActivityKit
// updates every second. Cadence + queue are already discrete; only last-fix
// needs bucketing.
function lastFixBucket(s: number | undefined): number | null {
  if (s == null) return null;
  if (s < 10) return 0;        // "just now"
  if (s < 60) return 1;        // "Ns ago"
  if (s < 120) return 2;       // <2m
  if (s < 5 * 60) return 3;    // 2-5m (amber)
  if (s < 15 * 60) return 4;   // 5-15m
  if (s < 60 * 60) return 5;   // 15-60m
  return 6;
}

function statsChanged(prev: LiveActivityStats | null, next: LiveActivityStats): boolean {
  if (!prev) return true;
  if (prev.intervalSeconds !== next.intervalSeconds) return true;
  if (prev.presetLabel !== next.presetLabel) return true;
  if (prev.presetKey !== next.presetKey) return true;
  if (prev.isPaused !== next.isPaused) return true;
  if (prev.isOffline !== next.isOffline) return true;
  if (prev.queueDepth !== next.queueDepth) return true;
  if (lastFixBucket(prev.lastFixAgeSeconds) !== lastFixBucket(next.lastFixAgeSeconds)) return true;
  return false;
}

export interface LADebugInfo {
  available: boolean;          // iOS native platform
  state: LAState;
  tracking: TrackingPhase;
  upload: UploadPhase;
  uploadPending: number;
  uploadCompleted: number;
  startedAt: string | null;     // ISO
  lastStartAt: string | null;
  lastUpdateAt: string | null;
  lastEndAt: string | null;
  lastBridgeIsTracking: boolean | null;
  lastError: string | null;
}

type Listener = (info: LADebugInfo) => void;

const MIN_UPDATE_MS = 800;

class LiveActivityController {
  private tracking: TrackingPhase = "off";
  private upload: UploadPhase = "idle";
  private uploadPending = 0;
  private uploadCompleted = 0;
  private startedAt: string | null = null;
  private currentState: LAState = "ended";
  private activityStarted = false;
  private lastBridgeIsTracking: boolean | null = null;
  private lastStartAt: string | null = null;
  private lastUpdateAt: string | null = null;
  private lastEndAt: string | null = null;
  private lastError: string | null = null;
  private lastUpdateTs = 0;
  private pendingTimer: ReturnType<typeof setTimeout> | null = null;
  private uploadCompleteDecayTimer: ReturnType<typeof setTimeout> | null = null;
  private statsTickTimer: ReturnType<typeof setInterval> | null = null;
  private lastStats: LiveActivityStats | null = null;
  private userDismissed = false;
  private listeners = new Set<Listener>();
  private initialized = false;

  /** Wire boot-time cleanup. Idempotent. */
  async init(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;
    // Seed the cached connection type + subscribe for changes. On change
    // while an Activity is live, push an update so the Island flips its
    // offline glyph within ~1s of losing/regaining signal.
    try {
      cachedConnection = await getConnectionType();
    } catch { /* ignore */ }
    try {
      addConnectionListener((t) => {
        cachedConnection = t;
        if (this.activityStarted) void this.pushUpdateNow();
      });
    } catch { /* ignore */ }
    try {
      await endOrphanLiveActivitiesIfSafe({ source: "controller-init" });
    } catch (e) {
      this.recordError("init/endAll", e);
    }
  }


  // --- public event API ------------------------------------------------------

  onTrackingChanged(on: boolean, startedAt: string | null): void {
    if (on) {
      // Tapping Start clears any previous user-dismissed latch so a new
      // Live Activity is allowed to be created.
      this.userDismissed = false;
    }
    this.tracking = on ? "active" : "off";
    if (on) {
      this.startedAt = startedAt ?? new Date().toISOString();
    } else if (!this.uploadActive()) {
      this.startedAt = null;
    }
    this.reconcile();
  }

  /**
   * User explicitly dismissed the Live Activity (tapped Stop tracking).
   * Forces immediate native dismissal AND latches the controller so that
   * subsequent upload-only signals can't resurrect the widget while the
   * user is not actively tracking.
   */
  userDismiss(opts?: { source?: string }): void {
    const source = opts?.source ?? "userDismiss";
    console.info("[live-activity] userDismiss", { source });
    this.userDismissed = true;
    if (this.uploadCompleteDecayTimer) {
      clearTimeout(this.uploadCompleteDecayTimer);
      this.uploadCompleteDecayTimer = null;
    }
    this.currentState = "ended";
    void this.callEnd(source);
    this.emit();
  }


  onTrackingPaused(paused: boolean): void {
    if (this.tracking === "off") return; // ignore pause when not tracking
    this.tracking = paused ? "paused" : "active";
    this.reconcile();
  }

  onUploadChanged(
    phase: UploadPhase,
    counts?: { pending?: number; completed?: number },
  ): void {
    this.upload = phase;
    if (counts?.pending !== undefined) this.uploadPending = counts.pending;
    if (counts?.completed !== undefined) this.uploadCompleted = counts.completed;
    this.reconcile();
  }

  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    l(this.debug());
    return () => { this.listeners.delete(l); };
  }

  debug(): LADebugInfo {
    return {
      available: isIOSPlatform(),
      state: this.currentState,
      tracking: this.tracking,
      upload: this.upload,
      uploadPending: this.uploadPending,
      uploadCompleted: this.uploadCompleted,
      startedAt: this.startedAt,
      lastStartAt: this.lastStartAt,
      lastUpdateAt: this.lastUpdateAt,
      lastEndAt: this.lastEndAt,
      lastBridgeIsTracking: this.lastBridgeIsTracking,
      lastError: this.lastError,
    };
  }

  async probe() {
    try {
      return await probeLiveActivity();
    } catch (e) {
      this.recordError("probe", e);
      return null;
    }
  }

  /**
   * Force a fresh stats push to the existing Live Activity. Used by the
   * dispatcher's setCadence path so the Island reflects the new preset
   * immediately without ending and restarting the Activity.
   */
  refreshStats(): void {
    if (!this.activityStarted) return;
    void this.pushUpdateNow({ force: true, source: "refreshStats" });
  }

  /**
   * User flipped the "Live Activity" preference in Settings. When turning
   * OFF mid-trip, tear down the visible activity but keep tracking running.
   * When turning ON during an active session, start a fresh activity.
   * Settings UI calls this after persisting via setLiveActivityEnabled(),
   * so prefs.ts has no reverse dependency on this controller.
   */
  onPreferenceChanged(enabled: boolean): void {
    console.info("[live-activity] preference changed", { enabled });
    if (!enabled) {
      if (this.activityStarted) {
        void this.callEnd("live-activity-disabled");
      }
      return;
    }
    // Enabled: only spin one up if a trip is genuinely in progress.
    if (
      !this.activityStarted &&
      (this.tracking === "active" || this.tracking === "paused")
    ) {
      // Clear any stale dismissal latch so the new activity is allowed.
      this.userDismissed = false;
      void this.callStart("live-activity-enabled");
    }
  }



  // --- internals -------------------------------------------------------------

  private uploadActive(): boolean {
    return this.upload === "uploading" || this.upload === "paused";
  }

  private reconcile(): void {
    let next = deriveLAState({ tracking: this.tracking, upload: this.upload });

    // Latch: once the user dismissed the activity, any upload-only signal
    // (the upload-drain handler firing `complete` / `uploading` after Stop)
    // must NOT resurrect the widget. The latch clears the moment tracking
    // turns back on.
    if (
      this.userDismissed &&
      this.tracking !== "active" &&
      this.tracking !== "paused"
    ) {
      next = "ended";
    }

    const prev = this.currentState;
    this.currentState = next;

    const wasActive = prev !== "ended";
    const nowActive = next !== "ended";

    // Cancel any pending upload_complete decay whenever the state moves
    // away from upload_complete (a new transition supersedes it).
    if (next !== "upload_complete" && this.uploadCompleteDecayTimer) {
      clearTimeout(this.uploadCompleteDecayTimer);
      this.uploadCompleteDecayTimer = null;
    }

    if (!wasActive && nowActive) {
      void this.callStart("reconcile/transition-to-active");
    } else if (wasActive && !nowActive) {
      void this.callEnd("reconcile/transition-to-ended");
    } else if (nowActive) {
      this.scheduleUpdate();
    }


    // Upload-only states no longer keep the Live Activity alive — uploads
    // drain silently in the background after Stop. The upload_complete decay
    // timer is intentionally removed; reconcile() ends the pill the instant
    // tracking flips off.


    this.emit();
  }

  private async callStart(source = "callStart"): Promise<void> {
    // User-level kill switch. When disabled we never call into ActivityKit
    // and never flip activityStarted — so probe/recover stays inert too.
    if (!getLiveActivityEnabled()) {
      console.info("[live-activity] start skipped", { source, reason: "disabled-by-user" });
      return;
    }
    if (this.activityStarted) {
      // Probe-and-recover: if Roava thinks we have an Activity but iOS has
      // none (system reaped it, user swiped it away on the Lock Screen, an
      // earlier bug ended it), clear the flag and start a fresh one. Only
      // runs while tracking is still active/paused — never resurrects after
      // a confirmed End Trip or intentional stop.
      try {
        const p = await probeLiveActivity();
        if (p && p.activeCount === 0) {
          console.info("[live-activity] probe found activeCount=0 — recreating", { source });
          this.activityStarted = false;
        } else {
          this.scheduleUpdate();
          return;
        }
      } catch {
        this.scheduleUpdate();
        return;
      }
    }
    const started = this.startedAt ? new Date(this.startedAt) : new Date();
    this.startedAt = started.toISOString();
    try {
      const stats = buildStatsSnapshot();
      stats.isPaused = this.tracking === "paused";
      this.lastStats = stats;
      console.info("[live-activity] callStart", { source, presetKey: stats.presetKey, isPaused: stats.isPaused });
      await startTrackingLiveActivity(started, stats);
      this.activityStarted = true;
      this.lastStartAt = new Date().toISOString();
      // Push initial isTracking immediately after start.
      await this.pushUpdateNow({ force: true, source: `${source}/post-start` });
      this.startStatsTick();
    } catch (e) {
      this.recordError("start", e);
    }
    this.emit();
  }



  private scheduleUpdate(): void {
    if (!this.activityStarted) return;
    const now = Date.now();
    const wait = Math.max(0, MIN_UPDATE_MS - (now - this.lastUpdateTs));
    if (this.pendingTimer) return;
    this.pendingTimer = setTimeout(() => {
      this.pendingTimer = null;
      void this.pushUpdateNow();
    }, wait);
  }

  private async pushUpdateNow(opts?: { force?: boolean; source?: string }): Promise<void> {
    const source = opts?.source ?? "pushUpdateNow";
    const nextTracking = mapToBridgeIsTracking(this.currentState);
    const nextStats = buildStatsSnapshot();
    nextStats.isPaused = this.tracking === "paused";
    const trackingChanged = this.lastBridgeIsTracking !== nextTracking;
    const statsDiff = statsChanged(this.lastStats, nextStats);
    if (!opts?.force && !trackingChanged && !statsDiff) {
      this.lastUpdateTs = Date.now();
      return;
    }
    try {
      console.info("[live-activity] pushUpdateNow", {
        source,
        isTracking: nextTracking,
        isPaused: nextStats.isPaused,
        presetKey: nextStats.presetKey,
      });
      await updateTrackingLiveActivity(nextTracking, nextStats);
      this.lastBridgeIsTracking = nextTracking;
      this.lastStats = nextStats;
      this.lastUpdateAt = new Date().toISOString();
      this.lastUpdateTs = Date.now();
    } catch (e) {
      this.recordError("update", e);
    }
    this.emit();
  }


  private startStatsTick(): void {
    if (this.statsTickTimer) return;
    // Re-evaluate stats every 30s while the Activity is live. The diff in
    // pushUpdateNow keeps us from spamming Activity.update — only meaningful
    // changes (bucket shifts, queue depth, preset change) actually push.
    this.statsTickTimer = setInterval(() => {
      if (!this.activityStarted) return;
      void this.pushUpdateNow();
    }, 30_000);
  }

  private stopStatsTick(): void {
    if (this.statsTickTimer) {
      clearInterval(this.statsTickTimer);
      this.statsTickTimer = null;
    }
  }

  private async callEnd(source = "callEnd"): Promise<void> {
    if (this.pendingTimer) { clearTimeout(this.pendingTimer); this.pendingTimer = null; }
    this.stopStatsTick();
    console.info("[live-activity] callEnd", {
      source,
      tracking: this.tracking,
      upload: this.upload,
      state: this.currentState,
    });
    try {
      await endTrackingLiveActivity({ source });
      this.activityStarted = false;
      this.lastBridgeIsTracking = null;
      this.lastStats = null;
      this.lastEndAt = new Date().toISOString();
      this.startedAt = null;
    } catch (e) {
      this.recordError("end", e);
    }
    this.emit();
  }


  private recordError(where: string, e: unknown): void {
    const msg = e instanceof Error ? e.message : String(e);
    this.lastError = `${where}: ${msg}`;
    console.debug("[live-activity-controller]", this.lastError);
  }

  private emit(): void {
    const info = this.debug();
    for (const l of this.listeners) {
      try { l(info); } catch { /* ignore listener errors */ }
    }
  }
}

function isIOSPlatform(): boolean {
  try {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
  } catch {
    return false;
  }
}

export const liveActivityController = new LiveActivityController();
